<script setup lang="ts">
import {
  formatting,
  LegacyAdjustmentCategory,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey
} from '@hantera/portal-app';
import {inject, ref, watch, watchEffect} from 'vue';
import OrderAdjustmentForm, {AdjustmentType} from '../OrderAdjustmentForm';
import Decimal from 'decimal.js';
import useBackend from '@/composition/useBackend';
import {EditableOrderRule} from '../editable-order-adjustment';
import {DecimalInputValue} from "@hantera/design-system";

const props = defineProps<{
  rule: EditableOrderRule
  type: AdjustmentType
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const backend = useBackend();
const { currency } = formatting;

const showForm = ref(false);
const category = ref<LegacyAdjustmentCategory>();
resetForm();

const value = ref<DecimalInputValue>({
  value: new Decimal(0),
  isPercentage: false,
})

watchEffect(() => {
  value.value = {
    value: props.rule.value,
    isPercentage: false
  }
})

function openForm() {
  if (props.rule.editable) {
    showForm.value = true;
  }
}

function resetForm() {
  props.rule.value = props.rule.originalValue;

  // Something seems buggy in Hantera backend and the fields are not returned, so
  // we'll match on rule name which is the best we can do right now.
  category.value = order.value.adjustmentCategories.find(r => r.name === props.rule.name);
}

watch(() => props.rule.value, () => {
  props.rule.affectedRows.forEach(r => {
    const proportion = r.originalValue.dividedBy(props.rule.originalValue);
    r.value = props.rule.value.mul(proportion);
  });
});

function closeForm() {
  resetForm();
  showForm.value = false;
}

async function submit() {
  if (props.rule.value.eq(new Decimal(0))) {
    await backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Remove adjustment of {value}',
      {
        value: props.rule.originalValue
      }
    )
      .command(legacyOrderCommands.removeAdjustment, {
        adjustmentId: props.rule.adjustmentId,
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'Removed adjustment \'{name}\' with value {value}',
        fields: {
          name: props.rule.name,
          value: props.rule.originalValue,
        }
      })
      .send();

    closeForm();
    return;
  }

  if (!category.value) {
    // TODO Add error state
    return;
  }

  const affectedRows = props.rule.affectedRows.filter(r => r.isRowBased);

  if (!order.value.includeVat) {
    // Turns out that accounting for VAT here causes more problems than it solves as legacy backend will mix things up / Kristoffer, 2025-03-29
    //priceWithVat = props.rule.value.mul(order.value.orderVat.div(100).add(1));
  }
  
  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    'Set adjustment from {oldValue} to {newValue}',
    {
      oldValue: props.rule.originalValue,
      newValue: value.value.value + (value.value.isPercentage ? '%' : ''),
    }
  )
    .command(legacyOrderCommands.addOrUpdateAdjustment, {
      adjustmentId: props.rule.adjustmentId,
      percentage: value.value.isPercentage ? value.value.value.div(100) : undefined,
      priceWithVat: value.value.isPercentage ? undefined : value.value.value,
      affectedRows: affectedRows.length > 0 ? affectedRows.map(r => r.rowId) : undefined,
    })
    // TODO: Add Description and it's UI (if users miss it - otherwise we should drop this for now, maybe some kind of notes will be needed in the future but "Description" is a bit misleading)
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: props.rule.adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: category.value.code,
    })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: props.rule.adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: category.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Set adjustment {name} from {oldValue} to {newValue}',
      fields: {
        oldValue: props.rule.originalValue,
        newValue: value.value.value + (value.value.isPercentage ? '%' : ''),
        name: category.value.name,
      }
    })
    .send();

  closeForm();
  return;
}

</script>

<template>
  <div class="summary__row summary__detail-row order-adjustment">
    <div
      class="summary__cell"
      v-if="!showForm"
    >
      <span
        :class="{
          'btn-editable': rule.editable
        }"
        @click="openForm"
        :title="rule.name"
      >
        {{ rule.name }}
      </span>
    </div>
    <div
      class="summary__cell"
      v-if="!showForm"
    >
      {{ currency(rule.value, order.currencyCode) }}
    </div>
    
    <OrderAdjustmentForm
      v-if="showForm"
      :type="type"
      v-model:selected-category="category"
      v-model:value="value"
      @submit="submit"
      @cancel="closeForm"
    />
  </div>
</template>

<style>
.order-adjustment {
  color: hsl(var(--color-text-50));
}

.order-adjustment .btn-editable:hover {
  color: hsl(var(--color-text));
}
</style>